<template>

  <v-data-table
      :headers="headers1"
      :items="orders"
      :items-per-page="itemsPerPage"
      item-key="orderId"
      class="elevation-1 orders"
      disable-sort-by
      :locale="systemLocale">
    <template v-slot:top>
      <v-progress-linear :active="loading" color="blue" indeterminate />
    </template>
    <!-- eslint-disable-next-line vue/valid-v-slot -->
    <template v-slot:item.organizationName="{ item }">
      <route-link-with-tip
          v-if="isAdmin"
          :text="item.organizationName"
          :tip="$t('Просмотр организации')"
          :to="{ path: `${urlRootPath}/organization/${item.organizationId}` }" />
      <span v-else>
        {{ item.organizationName }}
      </span>
    </template>
    <!-- eslint-disable-next-line vue/valid-v-slot -->
    <template v-slot:item.contactPerson="{ item }">
      <route-link-with-tip
          v-if="isAdmin"
          :text="item.contactPerson"
          :tip="$t('Просмотр пользователя')"
          :to="{ path: `${urlRootPath}/user/${item.userId}` }" />
      <span v-else>
        {{ item.contactPerson }}
      </span>
    </template>
    <!-- eslint-disable-next-line vue/valid-v-slot -->
    <template v-slot:item.orderDate="{ item }">
      {{ item.orderDate
        ? $moment(item.orderDate).format('DD.MM.YYYY HH:mm:ss')
        : null }}
    </template>
    <!-- eslint-disable-next-line  vue/no-template-shadow, vue/valid-v-slot -->
    <template v-slot:item.clientOrganizationFullName="{ item }">
      <template v-if="item.clientOrganizationFullName && item.clientOrganizationInn">
        {{ item.clientOrganizationFullName }} / {{ item.clientOrganizationInn }}
      </template>
    </template>
    <!-- eslint-disable-next-line  vue/no-template-shadow, vue/valid-v-slot -->
    <template v-slot:item.positionDescription="{ item }" dense>
      {{ item.positionDescription }}
    </template>
    <!-- eslint-disable-next-line  vue/no-template-shadow, vue/valid-v-slot -->
    <template v-slot:item.premium="{ item }" dense>
      {{ item.premium }}
    </template>
    <!-- eslint-disable-next-line  vue/no-template-shadow, vue/valid-v-slot -->
    <template v-slot:item.requestedPartsCount="{ item }" dense>
      {{ item.requestedPartsCount + "/" + item.satisfiedPartsCount }}
    </template>
    <!-- eslint-disable-next-line  vue/no-template-shadow, vue/valid-v-slot -->
    <template v-slot:item.satisfiedVolume="{ item }" dense>
      {{ item.satisfiedVolume }}
    </template>
    <!-- eslint-disable-next-line  vue/no-template-shadow, vue/valid-v-slot -->
    <template v-slot:item.reassignedSatisfiedVolume="{ item }" dense>
      <validatable-text-input
          v-if="item.isWinner && !readonly && isAdmin"
          hide-details="hideDetails"
          dense
          solo
          single-line
          :value="item.reassignedSatisfiedVolume"
          data-type="float"
          :rules="{
            required: true,
            max_value: MAX_FIX_PREMIUM_VALUE,
            min_value: MIN_FIX_PREMIUM_VALUE,
            regex: /^[0-9]{1,11}([.][0-9]{0,2})?$/
          }"
          @change="(volume) => reassignSatisfiedVolume(item, volume)" />
      <span v-else>
        {{ item.reassignedSatisfiedVolume }}
      </span>
    </template>
    <!-- eslint-disable-next-line vue/valid-v-slot -->
    <template v-slot:item.isWinner="{ item }">
      <v-checkbox
          v-if="item.satisfiedVolume > 0 || isAdmin"
          v-model="item.isWinner"
          dense
          :height="10"
          :readonly="readonly"
          :disabled="readonly"
          @change="winnerChange($event, item)" />
    </template>
  </v-data-table>
</template>

<style scoped>
.orders::v-deep td:nth-child(3) {
  word-break: break-all;
  max-width: 450px;
}

.orders::v-deep td:nth-child(1) {
  min-width: 200px;
}

.premiums::v-deep td:nth-child(1) {
  word-break: break-all;
  max-width: 550px;
}

.premiums::v-deep td:nth-child(2) {
  min-width: 150px;
}
</style>

<script>
  import RouteLinkWithTip from '@/components/reused/ui/RouteLinkWithTip.vue'
  import ValidatableTextInput from '@/components/reused/ui/ValidatableTextInput.vue'
  import { MAX_FIX_PREMIUM_VALUE, MIN_FIX_PREMIUM_VALUE } from '@/constants/business'

  export default {
    components: {
      RouteLinkWithTip,
      ValidatableTextInput
    },
    props: {
      isFixPrice: {
        type: Boolean,
        default: false
      },
      readonly: {
        type: Boolean,
        default: false
      },
      loading: {
        type: Boolean,
        default: false
      },
      orders: {
        type: Array,
        default: () => ([])
      },
      canSetWinnerNotSatisfied: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        MIN_FIX_PREMIUM_VALUE,
        MAX_FIX_PREMIUM_VALUE,
        itemsPerPage: 5,
        nestedItemsPerPage: -1,
        winners: []
      }
    },
    computed: {
      headers1 () {
        return [
          { text: this.$t('Организация'), sortable: false, value: 'organizationName' },
          { text: this.$t('Пользователь'), sortable: false, value: 'contactPerson' },
          { text: this.$t('Клиент'), sortable: false, value: 'clientOrganizationFullName' },
          { text: this.$t('Время выставления'), sortable: false, value: 'orderDate' },
          // { text: this.$t('Наименование позиции'), sortable: false, value: 'positionDescription' },
          { text: this.$t(this.isFixPrice ? 'Цена 1 (одной) углеродной единицы (с учетом НДС)' : 'Значение дифференциала'), sortable: false, value: 'premium' },
          { text: this.$t('Количество углеродных единиц (запрошено/удовлетворено)'), align: 'start', sortable: false, value: 'requestedPartsCount' },
          { text: this.$t('Объем в ценовом предложении (удовлетворено)'), align: 'start', sortable: false, value: 'satisfiedVolume' },
          { text: this.$t('Назначенный объем'), align: 'start', sortable: false, value: 'reassignedSatisfiedVolume' },
          { text: this.$t('Победитель'), sortable: false, value: 'isWinner' },
          { text: '', value: 'data-table-expand' }
        ]
      }
    },
    // watch: {
    //   value: {
    //     deep: true,
    //     immediate: true,
    //     handler (newVal) {
    //       if (newVal) {
    //         console.log('watch', newVal)
    //         this.initWinners(newVal)
    //       }
    //     }
    //   }
    // },
    methods: {
      initWinners: function (winners) {
        this.winners = winners
      },
      winnerChange: function ($event, item) {
        if ($event) {
          item.reassignedSatisfiedVolume = item.satisfiedVolume
          this.winners.push(item)
        } else {
          this.winners = this.winners.filter(w => w.orderId !== item.orderId)
        }
        this.$emit('input', this.winners)
      },
      reassignSatisfiedVolume (item, volume) {
        item.reassignedSatisfiedVolume = volume
      }
    }
  }
</script>
